import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * authStore — single source of truth for auth state.
 *
 * Persisted to localStorage so a refresh keeps the user logged in.
 * Persisted keys: token, user, memberships, activeDepartmentId.
 *
 * The `activeDepartmentId` is what the API client injects as the
 * X-Department-Id header on every dept-scoped call.  It is auto-set after
 * login when the user has a single membership, otherwise the LoginPage shows
 * a picker before redirecting into the app.
 *
 * Why Zustand instead of Context: Context re-renders every consumer on every
 * change.  Zustand's selector pattern means components subscribe to slices and
 * only re-render when their slice changes — important once the chat page is
 * streaming dozens of token chunks per second.
 */
export const useAuthStore = create(
  persist(
    (set, get) => ({
      // ─── State ─────────────────────────────────────────────────────────
      token: null,
      user: null,                    // { id, email, role, display_name }
      memberships: [],               // [{ department_id, department_code, department_name, bot_id, role }]
      activeDepartmentId: null,

      // ─── Actions ───────────────────────────────────────────────────────

      /**
       * Apply a successful /api/auth/v2/login response.
       * If the user has exactly one membership, auto-select it as active.
       * If they have multiple, the LoginPage routes them through a picker.
       */
      applyLogin: ({ token, user, memberships }) => {
        const single = memberships.length === 1 ? memberships[0] : null;
        set({
          token,
          user,
          memberships,
          activeDepartmentId: single ? single.department_id : null,
        });
      },

      /** Pick which department to act in. Called from the dept switcher. */
      setActiveDepartment: (departmentId) => {
        set({ activeDepartmentId: departmentId });
      },

      /** Clear all auth state. Used on logout and on 401 responses. */
      logout: () => {
        set({
          token: null,
          user: null,
          memberships: [],
          activeDepartmentId: null,
        });
      },

      // ─── Selectors / derived helpers ───────────────────────────────────

      /**
       * Effective role for the active department.
       * Backend's permission system applies the membership role inside the
       * department; the global user.role is only honoured for sys_admin.
       */
      getActiveMembership: () => {
        const { memberships, activeDepartmentId } = get();
        return memberships.find((m) => m.department_id === activeDepartmentId) || null;
      },

      isAuthenticated: () => {
        const { token, user } = get();
        return Boolean(token && user);
      },
    }),
    {
      name: "sadt-auth",
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        memberships: state.memberships,
        activeDepartmentId: state.activeDepartmentId,
      }),
    },
  ),
);
